/**
 * MOCK · ASSUMED (CT-5714) — the How to approach card of a record.
 *
 * Derived from the Intelligence seeds (`intelligence.mock.js`), the way
 * `entityHeader.mock.js` derives from `entities.mock.js`: the rows point at real
 * traits and insights, and the "N traits, M insights" counts are the ones the
 * Intelligence lists show.
 *
 * QA switch, read from the URL on every request (`?mockApproach=…`):
 *
 *   newContact   only Role and Send, "2 traits"
 *   dataGap      no Reach by
 *   traitsOnly   no insight behind any row
 *   restricted   the viewer sees only what they may see
 *   fail         the request fails
 *   empty        nothing to say
 *
 * `?mockNba=empty` ("no suggestions and no recap") empties this card too.
 */

import { getMockApproachSources } from './intelligence.mock.js';
import { getMockNbaScenario } from './nextBestAction.mock.js';

export const APPROACH_SCENARIOS = Object.freeze(['newContact', 'dataGap', 'traitsOnly', 'restricted', 'fail', 'empty']);

let scenarioOverride = null;

const readScenario = () => {
  if (scenarioOverride) return scenarioOverride;
  try {
    const value = new URLSearchParams(window.location.search).get('mockApproach');
    return APPROACH_SCENARIOS.includes(value) ? value : null;
  } catch {
    return null;
  }
};

const ROWS = {
  reach_by: { key: 'reach_by', label: 'Reach by', value: 'email, mornings; call if urgent', source: { kind: 'insight', id: 'i-channel' } },
  role: { key: 'role', label: 'Role', value: 'Technical Evaluator, not the signer', source: { kind: 'trait', id: 't3' } },
  send: { key: 'send', label: 'Send', value: 'written timelines — asked twice', source: { kind: 'trait', id: 't1' } },
  name: { key: 'name', label: 'Name', value: '“Sandy” in email, “Sandra” in proposals and contracts', source: { kind: 'trait', id: 't4' } },
};

export const getMockHowToApproach = ({ entityType, entityId }) => {
  const scenario = readScenario();
  if (scenario === 'fail') return { error: { status: 500, description: 'Mock approach failure (?mockApproach=fail).' } };

  const { traitIds, reachByInsightIds } = getMockApproachSources({ entityType, entityId });
  const updatedAt = new Date(Date.now() - 12 * 60 * 1000).toISOString();
  const body = (fields, traitCount, insightCount) => ({
    body: {
      entity_id: entityId,
      updated_at: updatedAt,
      trait_count: traitCount,
      insight_count: insightCount,
      restricted: scenario === 'restricted',
      fields,
    },
    meta: {},
  });

  if (scenario === 'empty' || getMockNbaScenario() === 'empty') return body([], 0, 0);
  if (scenario === 'newContact') return body([ROWS.role, ROWS.send], 2, 0);
  // No Reach by (img 18) and no insight (img 21) give the same card here: the
  // channel / timing insights behind Reach by are the only insights it reads.
  if (scenario === 'dataGap' || scenario === 'traitsOnly') {
    return body([ROWS.role, ROWS.send, ROWS.name], traitIds.length, 0);
  }
  return body([ROWS.reach_by, ROWS.role, ROWS.send, ROWS.name], traitIds.length, reachByInsightIds.length);
};

export const __setMockApproachScenario = (value) => {
  scenarioOverride = value;
};

export const __resetMockApproachForTests = () => {
  scenarioOverride = null;
};
